/* eslint-disable prettier/prettier */
import React, { useMemo } from 'react';
import { useAppSelector } from '@app/hooks/reduxHooks';
import { AppRoute } from '@app/interfaces/appRoute';
import { dashboardAclSelector, isProfessionalFisherSelector } from '@app/store/slices/userSlice';
import {
  IMPORT_PAPER_STATISTICS_PATH,
  BREEDING_JOURNAL_PATH,
  HOME_PATH,
  FISHING_INSPECTIONS_PATH,
  FISHING_OBSERVATIONS_PATH,
  FISH_TYPES_PATH,
  WATERBODIES_PATH,
  LICENSES_PATH,
  LICENSES_RETURN_PATH,
  FISHERS_PATH,
  SESSIONS_PATH,
  CATCHES_PATH,
  INVENTORY_PATH,
  NATURAL_BREEDING_PATH,
  STOCKING_PLAN_PATH,
  STOCKING_JOURNAL_PATH,
  STOCKING_YEARLY_SUMMARY_PATH,
  FISH_MOVEMENT_PATH,
  DRAUGHTINESS_PATH,
  WATER_QUALITY_PATH,
  PROFESSIONAL_FISHER_PATH,
  ALL_PROFESSIONAL_FISHERS_PATH,
  REPORTS_PATH,
  HUNTERS_PATH,
  HUNTER_PATH,
  KILLS_PATH,
  HUNTING_INSPECTIONS_PATH,
  HUNTING_LICENSES_PATH,
  ANALYTICS_FISHING_OPERATIONS_PATH,
  ANALYTICS_FISHING_MANAGEMENT_PATH,
  ANALYTICS_FISHING_HABITAT_PATH,
  ANALYTICS_PROFESSIONAL_FISHERS_PATH,
  ANALYTICS_HUNTING_OPERATIONS_PATH,
  ANALYTICS_SUMMARY_PATH,
  ANALYTICS_BASIC_STATISTICS_PATH_PATH,
  MEMBERS_PATH,
  ADMIN_USERS_PATH,
  ROLES_PATH,
  ORGANISATIONAL_UNITS_PATH,
  MESSAGES_PATH,
  WATERBODY_REGULATIONS_PATH,
  GEOGRAPHIC_AREAS_PATH,
  LICENSE_TYPES_PATH,
  NET_TYPES_PATH,
  FISHING_FEES_PATH,
  HUNTING_FEES_PATH,
  GENERAL_PATH,
  WORKFLOWS_PATH,
  USER_DETAILS_PATH,
} from '@app/constants/routerPaths';
import { withLoading } from '@app/hocs/withLoading.hoc';
import { UserDetailsPage } from '@app/pages/UserManagement/Members/UserDetails/UserDetailsPage';

// Dashboards
const DashboardPage = React.lazy(() => import('@app/pages/Dashboard/DashboardPage'));

// Top-Level Paths
const ProfessionalFisherPage = React.lazy(() => import('@app/pages/ProfessionalFisher/ProfessionalFisherPage'));
const AllProfessionalFishersPage = React.lazy(
  () => import('@app/pages/AllProfessionalFishers/AllProfessionalFishersPage'),
);
const ReportsPage = React.lazy(() => import('@app/pages/Reports/ReportsPage'));

// Fishing Operations
const LicensesPage = React.lazy(() => import('@app/pages/FishingOperations/Licenses/LicensesPage'));
const LicensesReturnPage = React.lazy(() => import('@app/pages/FishingOperations/LicensesReturn/LicensesReturnPage'));
const LicensePage = React.lazy(() => import('@app/pages/FishingOperations/License/LicensePage'));
const FishersPage = React.lazy(() => import('@app/pages/FishingOperations/Fishers/FishersPage'));
const FisherPage = React.lazy(() => import('@app/pages/FishingOperations/Fisher/FisherPage'));
const SessionsPage = React.lazy(() => import('@app/pages/FishingOperations/Sessions/SessionsPage'));
const SessionPage = React.lazy(() => import('@app/pages/FishingOperations/Session/SessionPage'));
const CatchesPage = React.lazy(() => import('@app/pages/FishingOperations/Catches/CatchesPage'));
const CatchPage = React.lazy(() => import('@app/pages/FishingOperations/Catch/CatchPage'));
const InspectionsPage = React.lazy(() => import('@app/pages/FishingOperations/Inspections/InspectionsPage'));
const InspectionPage = React.lazy(() => import('@app/pages/FishingOperations/Inspection/InspectionPage'));
const AllPaperStatisticsPage = React.lazy(
  () => import('@app/pages/FishingOperations/PaperStatistics/AllPaperStatisticsPage'),
);
const PaperStatisticsPage = React.lazy(() => import('@app/pages/FishingOperations/PaperStatistics/PaperStatistics'));

// Fishing Management
const InventoryPage = React.lazy(() => import('@app/pages/FishingManagement/Inventory/InventoryPage'));
const NewInventoryPage = React.lazy(() => import('@app/pages/FishingManagement/NewInventory/NewInventoryPage'));
const NaturalBreedingPage = React.lazy(
  () => import('@app/pages/FishingManagement/NaturalBreeding/NaturalBreedingPage'),
);
const StockingPlanPage = React.lazy(() => import('@app/pages/FishingManagement/StockingPlan/StockingPlanPage'));
const StockingJournalPage = React.lazy(
  () => import('@app/pages/FishingManagement/StockingJournal/StockingJournalPage'),
);
const StockingYearlySummaryPage = React.lazy(
  () => import('@app/pages/FishingManagement/StockingYearlySummary/StockingYearlySummaryPage'),
);
const BreedingJournalPage = React.lazy(
  () => import('@app/pages/FishingManagement/BreedingJournal/BreedingJournalPage'),
);

// Fishing Habitat
const FishMovementPage = React.lazy(() => import('@app/pages/FishingHabitat/FishMovement/FishMovementPage'));
const ObservationsPage = React.lazy(() => import('@app/pages/FishingHabitat/Observations/ObservationsPage'));
const ObservationPage = React.lazy(() => import('@app/pages/FishingHabitat/Observation/ObservationPage'));
const DraughtinessPage = React.lazy(() => import('@app/pages/FishingHabitat/Draughtiness/DraughtinessPage'));
const WaterQualityPage = React.lazy(() => import('@app/pages/FishingHabitat/WaterQuality/WaterQualityPage'));

// Hunting Operations
const HuntersPage = React.lazy(() => import('@app/pages/HuntingOperations/Hunters/HuntersPage'));
const HunterPage = React.lazy(() => import('@app/pages/HuntingOperations/Hunter/HunterPage'));
const KillsPage = React.lazy(() => import('@app/pages/HuntingOperations/Kills/KillsPage'));
const KillPage = React.lazy(() => import('@app/pages/HuntingOperations/Kill/KillPage'));
const HuntingInspectionsPage = React.lazy(
  () => import('@app/pages/HuntingOperations/HuntingInspections/HuntingInspectionsPage'),
);
const HuntingLicensesPage = React.lazy(
  () => import('@app/pages/HuntingOperations/HuntingLicenses/HuntingLicensesPage'),
);
const HuntingLicensePage = React.lazy(() => import('@app/pages/HuntingOperations/HuntingLicense/HuntingLicensePage'));

// Analytics
const AnalyticsFishingOperationsPage = React.lazy(
  () => import('@app/pages/Analytics/FishingOperations/FishingOperationsPage'),
);
const AnalyticsFishingManagementPage = React.lazy(
  () => import('@app/pages/Analytics/FishingManagement/FishingManagementPage'),
);
const AnalyticsFishingHabitatPage = React.lazy(() => import('@app/pages/Analytics/FishingHabitat/FishingHabitatPage'));
const AnalyticsProfessionalFishersPage = React.lazy(
  () => import('@app/pages/Analytics/ProfessionalFishers/ProfessionalFishersPage'),
);
const AnalyticsHuntingOperationsPage = React.lazy(
  () => import('@app/pages/Analytics/HuntingOperations/HuntingOperationsPage'),
);
const AnalyticsSummaryPage = React.lazy(() => import('@app/pages/Analytics/Summary/SummaryPage'));
const AnalyticsBasicStatisticsPage = React.lazy(
  () => import('@app/pages/Analytics/BasicStatistics/BasicStatisticsPage'),
);

// User Management
const MembersPage = React.lazy(() => import('@app/pages/UserManagement/Members/MembersPage'));
const RolesPage = React.lazy(() => import('@app/pages/UserManagement/Roles/RolesPage'));
const OrganisationalUnitsPage = React.lazy(
  () => import('@app/pages/UserManagement/OrganisationalUnits/OrganisationalUnitsPage'),
);
const MessagesPage = React.lazy(() => import('@app/pages/UserManagement/Messages/MessagesPage'));

// Master Data
const WaterBodiesPage = React.lazy(() => import('@app/pages/MasterData/WaterBodies/WaterBodiesPage'));
const WaterbodyRegulationsPage = React.lazy(
  () => import('@app/pages/MasterData/WaterBodyRegulations/WaterbodyRegulationsPage'),
);
const GeographicAreasPage = React.lazy(() => import('@app/pages/MasterData/GeographicAreas/GeographicAreasPage'));
const LicenseTypesPage = React.lazy(() => import('@app/pages/MasterData/LicenseTypes/LicenseTypesPage'));
const LicenseTypePage = React.lazy(() => import('@app/pages/MasterData/LicenseTypes/LicenseTypePage'));
const FishTypesPage = React.lazy(() => import('@app/pages/MasterData/FishTypes/FishTypesPage'));
const NetTypesPage = React.lazy(() => import('@app/pages/MasterData/NetTypes/NetTypesPage'));
const FishingFeesPage = React.lazy(() => import('@app/pages/MasterData/FishingFees/FishingFeesPage'));
const HuntingFeesPage = React.lazy(() => import('@app/pages/MasterData/HuntingFees/HuntingFeesPage'));

// Settings
const GeneralSettingsPage = React.lazy(() => import('@app/pages/Settings/GeneralSettingsPage'));
const WorkflowsSettingsPage = React.lazy(() => import('@app/pages/Settings/WorkflowsPage'));

// Top-Level Paths
const ProfessionalFisher = withLoading(ProfessionalFisherPage);
const AllProfessionalFishers = withLoading(AllProfessionalFishersPage);
const Reports = withLoading(ReportsPage);

// Fishing Operations
const Licenses = withLoading(LicensesPage);
const LicensesReturn = withLoading(LicensesReturnPage);
const License = withLoading(LicensePage);
const Fishers = withLoading(FishersPage);
const Fisher = withLoading(FisherPage);
const Sessions = withLoading(SessionsPage);
const Session = withLoading(SessionPage);
const Catches = withLoading(CatchesPage);
const Catch = withLoading(CatchPage);
const Inspections = withLoading(InspectionsPage);
const Inspection = withLoading(InspectionPage);
const PaperStatistics = withLoading(PaperStatisticsPage);
const AllPaperStatistics = withLoading(AllPaperStatisticsPage);

// Fishing Management
const Inventory = withLoading(InventoryPage);
const NewInventory = withLoading(NewInventoryPage);
const NaturalBreeding = withLoading(NaturalBreedingPage);
const StockingPlan = withLoading(StockingPlanPage);
const StockingJournal = withLoading(StockingJournalPage);
const StockingYearlySummary = withLoading(StockingYearlySummaryPage);
const BreedingJournal = withLoading(BreedingJournalPage);

// Fishing Habitat
const FishMovement = withLoading(FishMovementPage);
const Observations = withLoading(ObservationsPage);
const Observation = withLoading(ObservationPage);
const Draughtiness = withLoading(DraughtinessPage);
const WaterQuality = withLoading(WaterQualityPage);

// Hunting Operations
const Hunters = withLoading(HuntersPage);
const Hunter = withLoading(HunterPage);
const Kills = withLoading(KillsPage);
const Kill = withLoading(KillPage);
const HuntingInspections = withLoading(HuntingInspectionsPage);
const HuntingLicenses = withLoading(HuntingLicensesPage);
const HuntingLicenseDetail = withLoading(HuntingLicensePage);

// Analytics
const AnalyticsFishingOperations = withLoading(AnalyticsFishingOperationsPage);
const AnalyticsFishingManagement = withLoading(AnalyticsFishingManagementPage);
const AnalyticsFishingHabitat = withLoading(AnalyticsFishingHabitatPage);
const AnalyticsProfessionalFishers = withLoading(AnalyticsProfessionalFishersPage);
const AnalyticsHuntingOperations = withLoading(AnalyticsHuntingOperationsPage);
const AnalyticsSummary = withLoading(AnalyticsSummaryPage);
const AnalyticsBasicStatistics = withLoading(AnalyticsBasicStatisticsPage);

// User Management
const _Members = withLoading(MembersPage);
const UserDetails = withLoading(UserDetailsPage);
const Roles = withLoading(RolesPage);
const OrganisationalUnits = withLoading(OrganisationalUnitsPage);
const Messages = withLoading(MessagesPage);

// Master Data
const Waterbodies = withLoading(WaterBodiesPage);
const WaterbodyRegulations = withLoading(WaterbodyRegulationsPage);
const GeographicAreas = withLoading(GeographicAreasPage);
const LicenseTypes = withLoading(LicenseTypesPage);
const LicenseType = withLoading(LicenseTypePage);
const FishTypes = withLoading(FishTypesPage);
const NetTypes = withLoading(NetTypesPage);
const FishingFees = withLoading(FishingFeesPage);
const HuntingFees = withLoading(HuntingFeesPage);

// Settings
const GeneralSettings = withLoading(GeneralSettingsPage);
const WorkflowsSettings = withLoading(WorkflowsSettingsPage);

export const useAppRoutes = () => {
  const acl = useAppSelector(dashboardAclSelector);
  const isProfessionalFisher = useAppSelector(isProfessionalFisherSelector);

  const [homeRoute, routesConfig] = useMemo(() => {
    const routes: AppRoute[] = [];

    if (!acl) {
      return [null, routes];
    }

    const indexFeature = acl?.features.find((feature) => feature.isIndex);
    const indexView = indexFeature?.views.find((view) => view.isIndex);

    // Dashboards
    const dashboardHomeFeature = acl?.features.find((feature) => feature.name === 'dashboardHomeView');

    // Top-Level Paths
    const professionalFisherFeature = acl?.features.find((feature) => feature.name === 'professionalFisher');
    const allProfessionalFishersFeature = acl?.features.find((feature) => feature.name === 'allProfessionalFishers');
    const reportsFeature = acl?.features.find((feature) => feature.name === 'reports');

    // Fishing Operations
    const licensesFeature = acl?.features.find((feature) => feature.name === 'licenses');
    const _licensesReturnFeature = acl?.features.find((feature) => feature.name === 'licensesReturn');
    const fishersFeature = acl?.features.find((feature) => feature.name === 'fishers');
    const sessionsFeature = acl?.features.find((feature) => feature.name === 'sessions');
    const catchesFeature = acl?.features.find((feature) => feature.name === 'catches');
    const inspectionsFeature = acl?.features.find((feature) => feature.name === 'inspections');
    const paperStatisticsFeature = acl?.features.find((feature) => feature.name === 'paperStatistics');

    // Fishing Management
    const inventoryFeature = acl?.features.find((feature) => feature.name === 'inventory');
    const naturalBreedingFeature = acl?.features.find((feature) => feature.name === 'naturalBreeding');
    const stockingPlanFeature = acl?.features.find((feature) => feature.name === 'stocking-plan');
    const stockingJournalFeature = acl?.features.find((feature) => feature.name === 'stocking-journal');
    const stockingYearlySummaryFeature = acl?.features.find((feature) => feature.name === 'stocking-yearly-summary');
    const breedingJournalFeature = acl?.features.find((feature) => feature.name === 'breedingJournal');

    // Fishing Habitat
    const fishMovementFeature = acl?.features.find((feature) => feature.name === 'fishMovement');
    const observationsFeature = acl?.features.find((feature) => feature.name === 'observations');
    const draughtinessFeature = acl?.features.find((feature) => feature.name === 'draughtiness');
    const waterQualityFeature = acl?.features.find((feature) => feature.name === 'waterQuality');

    // Hunting Operations
    const huntersFeature = acl?.features.find((feature) => feature.name === 'hunters');
    const killsFeature = acl?.features.find((feature) => feature.name === 'games');
    const huntingInspectionsFeature = acl?.features.find((feature) => feature.name === 'huntingInspections');
    const huntingLicensesFeature = acl?.features.find((feature) => feature.name === 'huntingLicenses');

    // Analytics
    const analyticsFishingOperationsFeature = acl?.features.find(
      (feature) => feature.name === 'analyticsFishingOperations',
    );
    const analyticsFishingManagementFeature = acl?.features.find(
      (feature) => feature.name === 'analyticsFishingManagement',
    );
    const analyticsFishingHabitatFeature = acl?.features.find((feature) => feature.name === 'analyticsFishingHabitat');
    const analyticsProfessionalFishersFeature = acl?.features.find(
      (feature) => feature.name === 'analyticsProfessionalFishers',
    );
    const analyticsHuntingOperationsFeature = acl?.features.find(
      (feature) => feature.name === 'analyticsHuntingOperations',
    );
    const analyticsSummaryFeature = acl?.features.find((feature) => feature.name === 'analyticsSummary');
    const analyticsBasicStatisticsFeature = acl?.features.find(
      (feature) => feature.name === 'analyticsBasicStatistics',
    );

    // User Management
    const _membersFeature =
      acl?.features.find((feature) => feature.name === 'members') ||
      acl?.features.find((feature) => feature.name === 'adminUsers');
    const userDetailsFeature = acl?.features.find((feature) => feature.name === 'userDetails');
    const rolesFeature = acl?.features.find((feature) => feature.name === 'roles');
    const organisationalUnitsFeature = acl?.features.find((feature) => feature.name === 'organisationalUnits');
    const inboxMessagesFeature = acl?.features.find((feature) => feature.name === 'inboxMessages');

    // Master Data
    const waterBodiesFeature = acl?.features.find((feature) => feature.name === 'waterbodies');
    const waterbodyRegulationsFeature = acl?.features.find((feature) => feature.name === 'waterbodyRegulations');
    const geographicAreasFeature = acl?.features.find((feature) => feature.name === 'geographicAreas');
    const licenseTypesFeature = acl?.features.find((feature) => feature.name === 'licenseTypes');
    const fishTypesFeature = acl?.features.find((feature) => feature.name === 'fishTypes');
    const netTypesFeature = acl?.features.find((feature) => feature.name === 'netTypes');
    const fishingFeesFeature = acl?.features.find((feature) => feature.name === 'fishingFees');
    const huntingFeesFeature = acl?.features.find((feature) => feature.name === 'huntingFees');

    // Settings
    const generalSettingsFeature = acl?.features.find((feature) => feature.name === 'generalSettings');
    const workflowsSettingsFeature = acl?.features.find((feature) => feature.name === 'workflowsSettings');

    // Dashboards
    if (dashboardHomeFeature?.visible) {
      routes.push({
        path: HOME_PATH,
        component: DashboardPage,
        isIndex: !!dashboardHomeFeature.isIndex,
      });
    }

    // Top-Level Paths
    if (professionalFisherFeature?.visible && isProfessionalFisher) {
      routes.push({
        path: PROFESSIONAL_FISHER_PATH,
        component: ProfessionalFisher,
        isIndex: !!professionalFisherFeature.isIndex,
      });
    }

    if (allProfessionalFishersFeature?.visible && !isProfessionalFisher) {
      routes.push({
        path: ALL_PROFESSIONAL_FISHERS_PATH,
        component: AllProfessionalFishers,
        isIndex: !!allProfessionalFishersFeature.isIndex,
      });
    }

    if (reportsFeature?.visible) {
      routes.push({
        path: REPORTS_PATH,
        component: Reports,
        isIndex: !!reportsFeature.isIndex,
      });
    }

    // Fishing Operations
    if (licensesFeature?.visible) {
      routes.push({
        path: LICENSES_PATH,
        component: Licenses,
        isIndex: !!licensesFeature.isIndex,
      });

      routes.push({
        path: `${LICENSES_PATH}/:id`,
        component: License,
      });
    }

    // if (true) {
    routes.push({
      path: LICENSES_RETURN_PATH,
      component: LicensesReturn,
      isIndex: false,
    });
    // }

    if (fishersFeature?.visible) {
      routes.push({
        path: FISHERS_PATH,
        component: Fishers,
        isIndex: !!fishersFeature.isIndex,
      });
    }

    if (sessionsFeature?.visible) {
      if (sessionsFeature?.views.find((view) => view.name === 'tableView')?.visible) {
        routes.push({
          path: SESSIONS_PATH,
          component: Sessions,
          isIndex: !!sessionsFeature.isIndex,
        });
      }

      if (sessionsFeature?.views.find((view) => view.name === 'sessionView')?.visible) {
        routes.push({
          path: `${SESSIONS_PATH}/:id`,
          component: Session,
        });
      }
    }

    if (catchesFeature?.visible) {
      if (catchesFeature?.views.find((view) => view.name === 'tableView')?.visible) {
        routes.push({
          path: CATCHES_PATH,
          component: Catches,
          isIndex: indexFeature?.name === 'catches' && indexView?.name === 'tableView',
        });
      }

      if (catchesFeature?.views.find((view) => view.name === 'catchView')?.visible) {
        routes.push({
          path: `${CATCHES_PATH}/:id`,
          component: Catch,
        });
      }
    }

    if (inspectionsFeature?.visible) {
      if (inspectionsFeature?.views.find((view) => view.name === 'tableView')?.visible) {
        routes.push({
          path: FISHING_INSPECTIONS_PATH,
          component: Inspections,
          isIndex: indexFeature?.name === 'inspections' && indexView?.name === 'tableView',
        });
      }

      if (inspectionsFeature?.views.find((view) => view.name === 'inspectionView')?.visible) {
        routes.push({
          path: `${FISHING_INSPECTIONS_PATH}/:id`,
          component: Inspection,
        });
      }
    }

    if (paperStatisticsFeature?.visible) {
      routes.push({
        path: `${IMPORT_PAPER_STATISTICS_PATH}`,
        component: AllPaperStatistics,
        isIndex: !!paperStatisticsFeature.isIndex,
      });
      routes.push({
        path: `${IMPORT_PAPER_STATISTICS_PATH}/:id`,
        component: PaperStatistics,
      });
    }

    // Fishing Management
    if (inventoryFeature?.visible) {
      routes.push({
        path: INVENTORY_PATH,
        component: Inventory,
        isIndex: !!inventoryFeature.isIndex,
      });
    }

    if (naturalBreedingFeature?.visible) {
      routes.push({
        path: NATURAL_BREEDING_PATH,
        component: NaturalBreeding,
        isIndex: !!naturalBreedingFeature.isIndex,
      });
    }

    if (stockingPlanFeature?.visible) {
      routes.push({
        path: STOCKING_PLAN_PATH,
        component: StockingPlan,
        isIndex: !!stockingPlanFeature.isIndex,
      });
    }

    if (stockingJournalFeature?.visible) {
      routes.push({
        path: STOCKING_JOURNAL_PATH,
        component: StockingJournal,
        isIndex: !!stockingJournalFeature.isIndex,
      });
    }

    if (stockingYearlySummaryFeature?.visible) {
      routes.push({
        path: STOCKING_YEARLY_SUMMARY_PATH,
        component: StockingYearlySummary,
        isIndex: !!stockingYearlySummaryFeature.isIndex,
      });
    }

    if (breedingJournalFeature?.visible) {
      routes.push({
        path: BREEDING_JOURNAL_PATH,
        component: BreedingJournal,
        isIndex: false,
      });
    }

    // Fishing Habitat
    if (fishMovementFeature?.visible) {
      routes.push({
        path: FISH_MOVEMENT_PATH,
        component: FishMovement,
        isIndex: !!fishMovementFeature.isIndex,
      });
    }

    if (observationsFeature?.visible) {
      if (observationsFeature?.views.find((view) => view.name === 'tableView')?.visible) {
        routes.push({
          path: FISHING_OBSERVATIONS_PATH,
          component: Observations,
          isIndex: indexFeature?.name === 'observations' && indexView?.name === 'tableView',
        });
      }

      if (observationsFeature?.views.find((view) => view.name === 'observationView')?.visible) {
        routes.push({
          path: `${FISHING_OBSERVATIONS_PATH}/:id`,
          component: Observation,
        });
      }
    }

    if (draughtinessFeature?.visible) {
      routes.push({
        path: DRAUGHTINESS_PATH,
        component: Draughtiness,
        isIndex: !!draughtinessFeature.isIndex,
      });
    }

    if (waterQualityFeature?.visible) {
      routes.push({
        path: WATER_QUALITY_PATH,
        component: WaterQuality,
        isIndex: !!waterQualityFeature.isIndex,
      });
    }

    // Hunting Operations
    if (huntersFeature?.visible) {
      routes.push({
        path: HUNTERS_PATH,
        component: Hunters,
        isIndex: !!huntersFeature.isIndex,
      });
      routes.push({
        path: HUNTER_PATH,
        component: Hunter,
      });
    }

    if (killsFeature?.visible) {
      routes.push({
        path: KILLS_PATH,
        component: Kills,
        isIndex: !!killsFeature.isIndex,
      });
      routes.push({
        path: `${KILLS_PATH}/:id`,
        component: Kill,
      });
    }

    if (huntingInspectionsFeature?.visible) {
      routes.push({
        path: HUNTING_INSPECTIONS_PATH,
        component: HuntingInspections,
        isIndex: !!huntingInspectionsFeature.isIndex,
      });
    }

    if (huntingLicensesFeature?.visible) {
      if (huntingLicensesFeature?.views.find((view) => view.name === 'tableView')?.visible) {
        routes.push({
          path: HUNTING_LICENSES_PATH,
          component: HuntingLicenses,
        });
      }

      if (huntingLicensesFeature?.views.find((view) => view.name === 'huntingLicenseView')?.visible) {
        routes.push({
          path: `${HUNTING_LICENSES_PATH}/:id`,
          component: HuntingLicenseDetail,
        });
      }
    }

    // Analytics
    if (analyticsFishingOperationsFeature?.visible) {
      routes.push({
        path: ANALYTICS_FISHING_OPERATIONS_PATH,
        component: AnalyticsFishingOperations,
        isIndex: !!analyticsFishingOperationsFeature.isIndex,
      });
    }

    if (analyticsFishingManagementFeature?.visible) {
      routes.push({
        path: ANALYTICS_FISHING_MANAGEMENT_PATH,
        component: AnalyticsFishingManagement,
        isIndex: !!analyticsFishingManagementFeature.isIndex,
      });
    }

    if (analyticsFishingHabitatFeature?.visible) {
      routes.push({
        path: ANALYTICS_FISHING_HABITAT_PATH,
        component: AnalyticsFishingHabitat,
        isIndex: !!analyticsFishingHabitatFeature.isIndex,
      });
    }

    if (analyticsProfessionalFishersFeature?.visible) {
      routes.push({
        path: ANALYTICS_PROFESSIONAL_FISHERS_PATH,
        component: AnalyticsProfessionalFishers,
        isIndex: !!analyticsProfessionalFishersFeature.isIndex,
      });
    }

    if (analyticsHuntingOperationsFeature?.visible) {
      routes.push({
        path: ANALYTICS_HUNTING_OPERATIONS_PATH,
        component: AnalyticsHuntingOperations,
        isIndex: !!analyticsHuntingOperationsFeature.isIndex,
      });
    }

    if (analyticsSummaryFeature?.visible) {
      routes.push({
        path: ANALYTICS_SUMMARY_PATH,
        component: AnalyticsSummary,
        isIndex: !!analyticsSummaryFeature.isIndex,
      });
    }

    if (analyticsBasicStatisticsFeature?.visible) {
      routes.push({
        path: ANALYTICS_BASIC_STATISTICS_PATH_PATH,
        component: AnalyticsBasicStatistics,
        isIndex: !!analyticsBasicStatisticsFeature.isIndex,
      });
    }

    // User Management - AdminUsersPage is accessible at /members and /admin-users (backwards compatibility)
    const AdminUsersPage = React.lazy(() => import('../../pages/AdminUsers/AdminUsersPage'));
    const AdminUsers = withLoading(AdminUsersPage);

    //if (adminUsersFeature?.visible) {
    //const tableVisible = adminUsersFeature.views?.find((v) => v.name === 'tableView')?.visible;
    //if (tableVisible) {
    routes.push({
      path: MEMBERS_PATH,
      component: AdminUsers,
    });
    // Backwards compatibility: /admin-users also resolves to AdminUsersPage
    routes.push({
      path: ADMIN_USERS_PATH,
      component: AdminUsers,
    });
    //}
    //}

    // Removed old Members route - AdminUsersPage now handles /members

    if (userDetailsFeature?.visible) {
      if (userDetailsFeature?.views.find((view) => view.name === 'tableView')?.visible) {
        routes.push({
          path: USER_DETAILS_PATH,
          component: UserDetails,
          isIndex: !!userDetailsFeature.isIndex,
        });
      }
    }

    if (rolesFeature?.visible) {
      routes.push({
        path: ROLES_PATH,
        component: Roles,
        isIndex: !!rolesFeature.isIndex,
      });
    }

    if (organisationalUnitsFeature?.visible) {
      routes.push({
        path: ORGANISATIONAL_UNITS_PATH,
        component: OrganisationalUnits,
        isIndex: !!organisationalUnitsFeature.isIndex,
      });
    }

    if (inboxMessagesFeature?.visible) {
      if (inboxMessagesFeature?.views.find((view) => view.name === 'tableView')?.visible) {
        routes.push({
          path: MESSAGES_PATH,
          component: Messages,
          isIndex: !!inboxMessagesFeature.isIndex,
        });
      }
    }

    // Master Data
    if (waterBodiesFeature?.visible) {
      if (waterBodiesFeature?.views.find((view) => view.name === 'tableView')?.visible) {
        routes.push({
          path: WATERBODIES_PATH,
          component: Waterbodies,
          isIndex: !!waterBodiesFeature.isIndex,
        });
      }
    }
    if (waterbodyRegulationsFeature?.visible) {
      if (waterbodyRegulationsFeature?.views.find((view) => view.name === 'tableView')?.visible) {
        routes.push({
          path: WATERBODY_REGULATIONS_PATH,
          component: WaterbodyRegulations,
          isIndex: !!waterbodyRegulationsFeature.isIndex,
        });
      }
    }

    if (geographicAreasFeature?.visible) {
      routes.push({
        path: GEOGRAPHIC_AREAS_PATH,
        component: GeographicAreas,
        isIndex: !!geographicAreasFeature.isIndex,
      });
    }

    if (licenseTypesFeature?.visible) {
      routes.push({
        path: LICENSE_TYPES_PATH,
        component: LicenseTypes,
        isIndex: !!licenseTypesFeature.isIndex,
      });

      routes.push({
        path: `${LICENSE_TYPES_PATH}/:id`,
        component: LicenseType,
      });
    }

    if (inventoryFeature?.visible) {
      routes.push({
        path: INVENTORY_PATH,
        component: Inventory,
        isIndex: false,
      });
      routes.push({
        path: `${INVENTORY_PATH}/:id`,
        component: NewInventory,
        isIndex: false,
      });
    }

    if (fishTypesFeature?.visible) {
      if (fishTypesFeature?.views.find((view) => view.name === 'tableView')?.visible) {
        routes.push({
          path: FISH_TYPES_PATH,
          component: FishTypes,
          isIndex: !!fishTypesFeature.isIndex,
        });
      }
    }

    if (netTypesFeature?.visible) {
      routes.push({
        path: NET_TYPES_PATH,
        component: NetTypes,
        isIndex: !!netTypesFeature.isIndex,
      });
    }

    if (fishingFeesFeature?.visible) {
      if (fishingFeesFeature?.views.find((view) => view.name === 'tableView')?.visible) {
        routes.push({
          path: FISHING_FEES_PATH,
          component: FishingFees,
          isIndex: !!fishingFeesFeature.isIndex,
        });
      }
    }

    if (huntingFeesFeature?.visible) {
      if (huntingFeesFeature?.views.find((view) => view.name === 'tableView')?.visible) {
        routes.push({
          path: HUNTING_FEES_PATH,
          component: HuntingFees,
          isIndex: !!huntingFeesFeature.isIndex,
        });
      }
    }

    // Settings
    if (generalSettingsFeature?.visible) {
      routes.push({
        path: GENERAL_PATH,
        component: GeneralSettings,
        isIndex: !!generalSettingsFeature.isIndex,
      });
    }

    if (workflowsSettingsFeature?.visible) {
      routes.push({
        path: WORKFLOWS_PATH,
        component: WorkflowsSettings,
        isIndex: !!workflowsSettingsFeature.isIndex,
      });
    }

    if (fishersFeature?.visible) {
      routes.push({
        path: FISHERS_PATH,
        component: Fishers,
        isIndex: false,
      });

      routes.push({
        path: `${FISHERS_PATH}/:id`,
        component: Fisher,
      });
    }

    return [routes.find((route) => route.isIndex), routes];
  }, [acl, isProfessionalFisher]);

  return [homeRoute, routesConfig] as const;
};
